import React from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	CardContent,
	Stack,
	Box,
	TextField,
	Switch,
	Typography,
	Button,
	FormControl,
	Select,
	MenuItem
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import NumericStepper from 'components/NumericStepper';
import moment from 'moment';
import Card from 'components7/Card';
import utils from 'components/utils';

const DetailInfo = props => {
	const theme = useTheme();
	const translate = utils.useI18N();
	const loading = useStoreState(state => state.kit.detail.loading);
	const kit = useStoreState(state => state.kit.detail.data);
	const setName = useStoreActions(dispatch => dispatch.kit.detail.setName);
	const setInternalCode = useStoreActions(dispatch => dispatch.kit.detail.setInternalCode);
	const setEnabled = useStoreActions(dispatch => dispatch.kit.detail.setEnabled);
	const setDateFrom = useStoreActions(dispatch => dispatch.kit.detail.setDateFrom);
	const setDeliveryDate = useStoreActions(dispatch => dispatch.kit.detail.setDeliveryDate);
	const setAvailable = useStoreActions(dispatch => dispatch.kit.detail.setAvailable);
	const update = useStoreActions(dispatch => dispatch.kit.detail.update);
	const promotions = useStoreState(state => state.kit.detail.promotions);
	const selectPromotion = useStoreActions(dispatch => dispatch.kit.detail.selectPromotion);
	/* Calcolo la differenza con passaggi successivi altrimenti in caso di giorni vicini moment sbaglia il diff */
	const today = moment();
	const delivaryDateMoment = moment(kit.deliveryDate);
	const dateDiff = delivaryDateMoment.diff(today, 'days', true);
	const isTimeDelivered = dateDiff < 0;
	const isError = !kit.name || !kit.name.trim();

	return (
		<Card backgroundColor="#fff" title={translate('kit.details')} width="100%">
			<CardContent sx={{ paddingBottom: '0' }}>
				<Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={4}>
					<Stack
						direction={{ xs: 'column', md: 'row', sm: 'row' }}
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={2}
					>
						<Stack
							direction="column"
							justifyContent="center"
							alignItems="center"
							spacing={0}
							sx={{ width: '40px' }}
						>
							<Typography variant="titlePage" sx={{ textAlign: 'center', fontSize: '30px' }}>
								{kit.id}
							</Typography>
							<Typography
								variant="titleTab"
								sx={{ textAlign: 'center', marginLeft: '0 !important', marginTop: '-10px' }}
							>
								{translate('kit.id')}
							</Typography>
						</Stack>
						<TextField
							value={kit.name}
							label={translate('kit.name')}
							fullWidth={true}
							InputLabelProps={{ shrink: true }}
							error={!kit.name || !kit.name.trim()}
							helperText={
								!kit.name || !kit.name.trim() ? translate('common.formValidation.required') : ''
							}
							onChange={e => setName(e.target.value)}
							disabled={isTimeDelivered}
							sx={{ width: '400px !important' }}
						/>
						<TextField
							value={kit.internalCode}
							label={translate('kit.internalCode')}
							fullWidth={true}
							InputLabelProps={{ shrink: true }}
							disabled={isTimeDelivered}
							onChange={e => setInternalCode(e.target.value.replace(/[^\w\s]/gi, ''))}
						/>
					</Stack>
					<Stack
						direction={{ xs: 'column', md: 'row', sm: 'row' }}
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={2}
						sx={{ width: '100%' }}
					>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								label={translate('kit.dateFrom')}
								value={kit.dateFrom ? moment(kit.dateFrom) : null}
								format="DD MMMM YYYY"
								onChange={setDateFrom}
								disablePast
								disabled={isTimeDelivered}
								error={!kit.dateFrom || !moment(kit.dateFrom).isValid()}
								helperText={translate('common.dateInvalid')}
								sx={{ width: '100%', maxWidth: '200px' }}
							/>
							<DatePicker
								label={translate('kit.deliveryDate')}
								value={kit.deliveryDate ? moment(kit.deliveryDate) : null}
								format="DD MMMM YYYY"
								onChange={setDeliveryDate}
								disablePast
								disabled={isTimeDelivered}
								error={
									!kit.deliveryDate ||
									!moment(kit.deliveryDate).isValid() ||
									moment(kit.dateFrom).isAfter(moment(kit.deliveryDate))
								}
								helperText={translate('common.dateInvalid')}
								sx={{ width: '100%', maxWidth: '200px' }}
							/>
						</LocalizationProvider>
						<FormControl variant="outlined" sx={{ width: '100%' }}>
							<Select
								value={kit.promoCode}
								displayEmpty
								disabled={isTimeDelivered}
								onChange={event => selectPromotion(event.target.value)}
								sx={{ maxWidth: '300px' }}
							>
								{promotions.map((item, index) => (
									<MenuItem key={item.sapPromoId} value={item.sapPromoId}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
					<Stack
						direction={{ xs: 'row', md: 'row', sm: 'row' }}
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={4}
					>
						<Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={0}>
							<Typography variant="titleTab">{translate('kit.enabled')}</Typography>
							<Switch checked={kit.enabled} onChange={e => setEnabled(e.target.checked)} />
						</Stack>
						<Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
							<Typography variant="titleTab">{translate('kit.available')}</Typography>
							<Box sx={{ width: '90px' }}>
								<NumericStepper
									removeDot
									value={kit.available}
									max={9999}
									precision={0}
									min={1}
									strict
									disabled={isTimeDelivered}
									onChange={numberAsValue => setAvailable(numberAsValue)}
								/>
							</Box>
						</Stack>
						<Stack direction="column" justifyContent="flex-start" alignItems="center" spacing={0}>
							<Typography variant="titleTab">{translate('kit.committed')}</Typography>
							<Typography variant="titlePage" sx={{ fontSize: '30px', marginTop: '-5px' }}>
								{kit.committed}
							</Typography>
						</Stack>
					</Stack>
					<Box sx={{ width: '100%', marginTop: '10px !important', textAlign: 'right' }}>
						<Button variant="dark" disabled={loading || isError} onClick={update}>
							{translate('common.save')}
						</Button>
					</Box>
				</Stack>
			</CardContent>
		</Card>
	);
};

export default DetailInfo;
